/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  Pipe,
  PipeTransform,
  signal,
} from '@angular/core';
import {
  CalendarClickEvent,
  CalendarDropTask,
  CalendarResourcesDay,
  CalendarTask,
  CommonUtils,
  HourSelectionEvent,
  ResourceDaySelectionEvent,
  Time,
} from '@infodata-group/inf-commons';
import { CalendarView } from '../calendar-view';
import dayjs from 'dayjs';
import { Telework } from '../telework';
import { TeleworkService } from '../telework.service';
import { DayNotWorkService } from '../day-not-work.service';

@Pipe({
  name: 'removeBr',
  standalone: true,
})
export class RemoveBrPipe implements PipeTransform {
  transform(value: string): string {
    if (!value) {
      return '';
    }
    return value.replace(/<br\s*\/?>/gi, ', ');
  }
}

@Component({
  selector: 'infl-calendar-view-resource',
  templateUrl: './calendar-view-resource.component.html',
  standalone: true,
  styleUrls: ['./calendar-view-resource.component.scss'],
  imports: [RemoveBrPipe],
})
export class CalendarViewResourceComponent implements CalendarView {
  public sections = input.required<CalendarResourcesDay[]>();
  public tasks = input.required<CalendarTask[]>();
  public canDrag = input.required<boolean>();
  public canSelect = input.required<boolean>();
  public enable = input.required<boolean>();
  public dragByOutside = input.required<CalendarDropTask<any>>();
  public dayTimeStart = input.required<Time>();
  public dayTimeEnd = input.required<Time>();
  public teleworks = input<Telework[]>([]);
  public resource = input.required<string>();

  public sectionSelection = output<ResourceDaySelectionEvent>();
  public sectionPreselection = output<ResourceDaySelectionEvent>();
  public clickOnSelection = output<ResourceDaySelectionEvent>();
  public dblClickOnSelection = output<ResourceDaySelectionEvent>();
  public taskClick = output<CalendarClickEvent>();
  public taskDblClick = output<CalendarClickEvent>();
  public taskHover = output<CalendarTask>();
  public taskUpdate = output<CalendarTask>();
  public taskCreation = output<Date>();
  public dragTask = output<{
    task: CalendarTask;
    shift: number;
  }>();
  public dropOutside = output<CalendarResourcesDay>();

  public selectedSection = signal<CalendarResourcesDay>(null);
  public viewTasks: { task: CalendarTask; style: any }[][] = [];

  private draggingTask: { task: CalendarTask; style: any };

  private readonly teleworkService = inject(TeleworkService);
  private readonly dayNotWorkService = inject(DayNotWorkService);

  public cellsBackgroundColor = computed<string[]>(() => {
    if (this.sections()) {
      const styles = [];
      for (const section of this.sections()) {
        const isDayWork = this.dayNotWorkService.isDayWork(section.date);
        if (!isDayWork) {
          styles.push(this.dayNotWorkService.dayNotWorkColor);
        } else {
          styles.push(
            this.checkTelework(section)
              ? this.teleworkService.teleworkColor
              : '#FFF',
          );
        }
      }
      return styles;
    }
    return [];
  });

  constructor() {
    effect(() => {
      if (this.sections() && this.tasks()) {
        this.generateTasks();
        this.clearLastSectionHighlighted();
      }
    });
  }

  private checkTelework(section: CalendarResourcesDay) {
    if (!this.teleworks()) {
      return false;
    }
    return !!this.teleworks().find(
      (telework) =>
        telework.resourceId === this.resource() &&
        telework.date === CommonUtils.convertDateToString(section.date),
    );
  }

  public generateTasks() {
    this.draggingTask = null;
    this.viewTasks = [];
    if (this.tasks()) {
      for (const task of this.tasks()) {
        this.generateTask(task);
      }
      for (let i = 0; i < this.viewTasks.length; ++i) {
        if (this.viewTasks[i]) {
          this.sections()[i].setCount(this.viewTasks[i].length);
          this.sections()[i].setDuration(
            this.viewTasks[i].map((vt) => vt.task),
          );
        }
      }
    }
  }

  public onTaskClick(
    event: MouseEvent,
    task: CalendarTask,
    dblClick?: boolean,
  ) {
    event.preventDefault();
    event.stopPropagation();
    if (dblClick) {
      this.taskDblClick.emit(new CalendarClickEvent(event, task));
    } else {
      this.taskClick.emit(new CalendarClickEvent(event, task));
    }
  }

  public refresh() {
    this.generateTasks();
  }

  onDragStart(task: { task: CalendarTask; style: any }) {
    if (this.canDrag() && task.task.enable) {
      this.draggingTask = task;
      this.dragTask.emit({ task: task.task, shift: 0 });
    }
  }

  onDropTask(section: CalendarResourcesDay) {
    if (this.canDrag()) {
      if (this.draggingTask) {
        let start = this.draggingTask.task.dateStart.clone();
        start = start
          .set('month', section.date.getMonth())
          .set('year', section.date.getFullYear())
          .date(section.date.getDate());
        this.draggingTask.task.updateDateStart(start);
        this.taskUpdate.emit(this.draggingTask.task);
        this.refresh();
      } else if (this.dragByOutside()) {
        const date = section.date;
        this.taskCreation.emit(date);
      } else {
        this.dropOutside.emit(section);
      }
    }
  }

  deselect(): void {
    this.clearSelection();
  }

  public clearLastSectionHighlighted() {
    const lastSectionDraggedOverIndex = this.cellsBackgroundColor().findIndex(
      (cell) => cell === '#FFF2DA',
    );
    if (lastSectionDraggedOverIndex != -1) {
      if (
        !this.dayNotWorkService.isDayWork(
          this.sections()[lastSectionDraggedOverIndex].date,
        )
      ) {
        this.cellsBackgroundColor()[lastSectionDraggedOverIndex] =
          this.dayNotWorkService.dayNotWorkColor;
      } else {
        this.cellsBackgroundColor()[lastSectionDraggedOverIndex] =
          this.checkTelework(this.sections()[lastSectionDraggedOverIndex])
            ? this.teleworkService.teleworkColor
            : '#FFF';
      }
    }
  }

  protected onDragOver(event: DragEvent, index: number) {
    event.preventDefault();
    this.clearLastSectionHighlighted();
    this.cellsBackgroundColor()[index] = '#FFF2DA';
  }

  private getResourceDaySelectionEvent(
    section: CalendarResourcesDay,
    event: MouseEvent,
  ): ResourceDaySelectionEvent {
    const hourSelection = new HourSelectionEvent(
      { start: this.dayTimeStart(), end: this.dayTimeEnd(), resource: null },
      event,
    );
    return new ResourceDaySelectionEvent(section, hourSelection);
  }

  selectSection(section: CalendarResourcesDay, event: MouseEvent) {
    if (this.canSelect()) {
      if (this.selectedSection() !== section) {
        this.selectedSection.set(section);
        this.sectionSelection.emit(
          this.getResourceDaySelectionEvent(section, event),
        );
      } else {
        this.clickOnSelection.emit(
          this.getResourceDaySelectionEvent(section, event),
        );
      }
    }
  }

  selectByDblClick(section: CalendarResourcesDay, event: MouseEvent) {
    this.dblClickOnSelection.emit(
      this.getResourceDaySelectionEvent(section, event),
    );
  }

  onOverZone(section: CalendarResourcesDay, event: MouseEvent) {
    this.sectionPreselection.emit(
      this.getResourceDaySelectionEvent(section, event),
    );
  }

  findTask(id: string): { style: any; task: CalendarTask } {
    for (const v of this.viewTasks) {
      const t = v.find((vt) => vt.task.id === id);
      if (t) {
        return t;
      }
    }
    return null;
  }

  getScrollPosition(): { x: number; y: number } {
    return null;
  }

  setScrollPosition(x: number, y: number) {
    // DO Nothing
  }

  private generateTask(task: CalendarTask) {
    const dayCount = task.dayDuration();
    let date = task.dateStart;
    const style = {
      color: task.color,
      borderColor: task.backgroundColor,
      top: 'O',
    };
    for (let i = 0; i < dayCount; ++i) {
      const index = this.sections().findIndex((s) =>
        CommonUtils.compareDate(date.toDate(), s.date),
      );
      date = dayjs(date).add(1, 'd');
      if (index >= 0) {
        if (!this.viewTasks[index]) {
          this.viewTasks[index] = [];
        }
        style.top = `${this.viewTasks[index].length * 27}px`;
        this.viewTasks[index].push({
          task: task,
          style: style,
        });
      }
    }
  }

  private clearSelection() {
    this.selectedSection.set(null);
  }

  public scrollToDate(date: Date): void {}
}
